import { useState, useEffect, useCallback } from "react";

import { Breadcrumb, Button, Input, Spin } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { isAlphanumericAndSpace } from "../utils";

function EditClaimTopic({ service }) {
  const navigate = useNavigate();
  const { topicId } = useParams();
  const [displayName, setDisplayName] = useState("");
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchClaimTopic = useCallback(async () => {
    try {
      const result = await service.getClaimTopicById(topicId);
      const record = result?.[0];

      if (!record) {
        toast.error("This compliance rule could not be found.");
        navigate("/topics");
        return;
      }

      setTopic(record.attributes.topic);
      setDisplayName(record.attributes.displayName || "");
    } catch (e) {
      console.error("Failed to load compliance rule:", e);
      toast.error("Failed to load compliance rule");
    } finally {
      setLoading(false);
    }
  }, [service, topicId, navigate]);

  useEffect(() => {
    fetchClaimTopic();
  }, [fetchClaimTopic]);

  function validateDisplayName(name) {
    if (name.trim() === "") {
      toast.error("Display Name is required");
      return false;
    }

    if (!isAlphanumericAndSpace(name)) {
      toast.error("Display Name must contain only alphanumeric characters");
      return false;
    }

    return true;
  }

  const saveDisplayName = async () => {
    const trimmedDisplayName = displayName.trim();

    if (!validateDisplayName(trimmedDisplayName)) {
      return;
    }

    setSaving(true);

    try {
      await toast.promise(
        service.updateClaimTopic({
          topic: String(topic),
          displayName: trimmedDisplayName,
        }),
        {
          pending: "Saving Compliance Rule...",
          success: `Successfully renamed to ${trimmedDisplayName}`,
          error: "An error occurred while saving the Compliance Rule",
        }
      );

      navigate("/topics");
    } catch (error) {
      console.error("Unexpected error while saving compliance rule:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spin tip="Loading..." />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb
        className="bg-transparent"
        items={[{ title: <Link to={"/"}>Home</Link> }, { title: <Link to={"/topics"}>Compliance Rules</Link> }, { title: "Edit" }]}
      />
      <p className="text-xl p-6">Edit Compliance Rule</p>
      <hr />
      <div className="p-3 mt-2">
        <div>
          <label htmlFor="claimTopicDisplayName">Compliance Rule Display Name *</label>
          <div className="mt-3 ml-1 relative w-full flex border rounded-lg p-0">
            <Input
              id="claimTopicDisplayName"
              value={displayName}
              className="border w-full p-2 rounded-lg text-xl"
              placeholder="Enter Display Name"
              type="text"
              maxLength={32}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <p className="absolute right-5 top-3">{displayName.length}/32</p>
          </div>
          <p>User-friendly name that describes the schema. Shown to end-users.</p>
        </div>
        <div className="mt-10 mb-6">
          <label htmlFor="claimTopicHiddenName">TopicID hidden name *</label>
          <div className="mt-3 ml-1 relative w-full flex border rounded-lg p-0">
            <Input
              id="claimTopicHiddenName"
              value={topic}
              readOnly
              className="border w-full p-2 rounded-lg text-xl"
              type="text"
              style={{ backgroundColor: "white", cursor: "default" }}
            />
          </div>
          <p>The topic id is set on-chain when the rule is created and cannot be changed.</p>
        </div>
        <div className="flex justify-end max-[600px]:justify-center">
          <Button
            className="max-[600px]:w-[60%] min-w-max text-center font-semibold rounded h-11 bg-[#7F56D9] text-white"
            onClick={saveDisplayName}
            disabled={saving}
          >
            Save Compliance Rule
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EditClaimTopic;
